// Submitting the search bar, and where it goes (item #17172).
//
// Item #16450 gave the attached surface a submit button, and a submit button
// implies an explicit submit -- which implies a destination. #17170 stored the
// pair (submitAction, searchResultsUrl) and #17171 validated the destination at
// SAVE, on the server, whole-origin.
//
// So this module composes and performs; it does not decide policy. That split
// is decision #5057's: the server owns what is allowed, the browser only
// carries it out. Re-checking the origin here would be a second copy of a rule
// that already has one home, and the copy in the browser is the one an attacker
// gets to edit.

export const SUBMIT_ACTION_NONE = 'none';
export const SUBMIT_ACTION_RESULTS_URL = 'results_url';

// resolveSubmitTarget returns the URL a submit should navigate to, or null when
// submitting should do nothing -- which is the default and today's behaviour.
//
// Four ways to get null, and they are separate questions rather than one
// truthiness check:
//
//   - the action is not results_url (including absent, for a bundle newer than
//     its instance);
//   - there is no destination. #17170's pair invariant makes this combination
//     unstorable, but navigating to a bare "?q=..." would reload the customer's
//     own page with the widget's query pinned to it, which is a worse failure
//     than doing nothing;
//   - the query is empty or blank. Item #16443 settled the same question for
//     the dropdown: a submit on an empty box must not act.
export function resolveSubmitTarget(config = {}, query = '') {
  if (config?.submitAction !== SUBMIT_ACTION_RESULTS_URL) return null;

  const destination = typeof config?.searchResultsUrl === 'string' ? config.searchResultsUrl : '';
  if (!destination) return null;

  const q = String(query ?? '').trim();
  if (!q) return null;

  // Split on the fragment first. A query string appended after "#" is part of
  // the fragment, so it reaches neither the server nor use_search's ?q= read.
  const hashAt = destination.indexOf('#');
  const before = hashAt === -1 ? destination : destination.slice(0, hashAt);
  const hash = hashAt === -1 ? '' : destination.slice(hashAt);

  // The destination may carry parameters of its own. Appending with "?"
  // unconditionally turns /search?ref=nav into one parameter named "ref" whose
  // value is "nav?q=ghost".
  const separator = before.includes('?') ? '&' : '?';

  return `${before}${separator}q=${encodeURIComponent(q)}${hash}`;
}

// navigateOnSubmit performs the navigation and reports whether it did.
//
// The boolean is load-bearing rather than a courtesy: the caller opens the
// attached dropdown when the submit was NOT consumed, so "did not navigate" has
// to be observable to it.
//
// Assignment to location.href rather than location.assign(), which is the
// idiom index.jsx already uses to open a result. Both navigate; using one of
// them means the test stand-in for window.location has one shape.
export function navigateOnSubmit(config, query) {
  const target = resolveSubmitTarget(config, query);
  if (!target) return false;
  if (typeof window === 'undefined' || !window.location) return false;

  window.location.href = target;
  return true;
}
